import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { appConfig } from 'src/config';
import { Campaign as BaseCampaign } from 'src/types/campaign';
import { CampaignApiRequest } from '@pages/campaign/campaign-form/campaign-form.types';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';

export enum CampaignStatusV2 {
  PLANNED = 'PLANNED',
  CANCELED = 'CANCELED',
  FINISHED = 'FINISHED',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  DRAFT = 'DRAFT',
  FAILED = 'FAILED',
}
export interface Campaign extends BaseCampaign {
  senderName: string;
  statusV2: CampaignStatusV2;
}

export enum CampaignLogStatus {
  DELIVERED = 'DELIVERED',
  SENT = 'SENT',
  UNDELIVERED = 'UNDELIVERED',
  FAILED = 'FAILED',
  REJECTED = 'REJECTED',
  SENT_UNDELIVERED = 'SENT_UNDELIVERED',
  UNKNOWN = 'UNKNOWN',
  BLOCKED_OVER_CREDIT_LIMIT = 'BLOCKED_OVER_CREDIT_LIMIT',
  BLOCKED_NOT_IN_PACKAGE = 'BLOCKED_NOT_IN_PACKAGE',
  OPT_OUT = 'OPT_OUT',
  SENT_NO_FINAL_DLR = 'SENT_NO_FINAL_DLR',
  ACCEPTED = 'ACCEPTED',
  PREPARED = 'PREPARED',
  NOT_WHITELISTED = 'NOT_WHITELISTED',
  ERROR = 'ERROR',
  DELETED = 'DELETED',
  ACK = 'ACK',
  READ = 'READ',
  LOGONLY = 'LOGONLY',
  SPAM = 'SPAM',
  DEMO_LIMIT_EXCEEDED = 'DEMO_LIMIT_EXCEEDED',
  CALLED = 'CALLED',
  ENROUTE = 'ENROUTE',
  PARTIALLY_DELIVERED = 'PARTIALLY_DELIVERED',
  NEW = 'NEW',
}
export interface CampaignLog {
  createDateTime: string;
  createDateTimestamp: number;
  id: number;
  lastStatus: CampaignLogStatus;
  message: string;
  numParts: number;
  receiver: string;
  sender: string;
  statusDetails: string;
  fallbackStatus?: CampaignLogStatus;
  fallbackStatusDetails?: string;
}

export const campaignsApi = createApi({
  tagTypes: ['Campaign'],
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl: `${appConfig.URL_REST}`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Legacy REST can return HAL+JSON; accept both to avoid 406 Not Acceptable
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json, application/hal+json');
      }
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json; charset=UTF-8');
      }

      return headers;
    },
  })),
  reducerPath: 'campaignsApi',
  endpoints: (builder) => ({
    getCampaign: builder.query<Campaign, number>({
      query: (id) => ({
        url: `campaigns/${id}`,
      }),
      providesTags: [{ type: 'Campaign', id: 'campaign' }],
    }),
    createSenderId: builder.mutation<void, string>({
      query: (number) => ({
        url: 'campaigns/sender-ids',
        method: 'POST',
        body: { number, staticId: false },
      }),
    }),
    createCampaign: builder.mutation<Campaign, CampaignApiRequest>({
      query: (campaign) => ({
        url: 'campaigns',
        method: 'POST',
        body: campaign,
      }),
      invalidatesTags: [{ type: 'Campaign', id: 'campaigns' }],
    }),
    updateCampaign: builder.mutation<Campaign, CampaignApiRequest & { id: Campaign['id'] }>({
      query: ({ id, ...campaign }) => ({
        url: `campaigns/${id}`,
        method: 'PUT',
        body: campaign,
      }),
      invalidatesTags: [
        { type: 'Campaign', id: 'campaigns' },
        { type: 'Campaign', id: 'campaign' },
      ],
    }),
    deleteCampaign: builder.mutation<void, Campaign['id']>({
      query: (id) => ({
        url: `campaigns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Campaign', id: 'campaigns' }],
    }),
    startCampaign: builder.query<void, number>({
      query: (id) => ({
        url: `campaign/send/${id}`,
        method: 'POST',
      }),
    }),
    getLogs: builder.query<
      CampaignLog[],
      {
        page: number;
        size: number;
        sort: string;
        campaignId: number;
        full: boolean;
        /** Optional delivery status filter (maps to CampaignLogSearchCriteria.lastStatus). */
        lastStatus?: CampaignLogStatus;
      }
    >({
      query: (params) => ({
        url: 'price-lists/campaignLog',
        params,
      }),
      transformResponse: (response: { content: CampaignLog[] }) => response.content,
    }),
  }),
});

export const {
  useGetCampaignQuery,
  useCreateSenderIdMutation,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useLazyStartCampaignQuery,
  useLazyGetLogsQuery,
  useDeleteCampaignMutation,
} = campaignsApi;
